export const DOC_TYPES = {
  pan: { label: "PAN Card", color: "red" },
  photo: { label: "Passport-size Photo", color: "violet" },
  aadhaar: { label: "Aadhaar Card", color: "blue" },
  voter: { label: "Voter ID", color: "blue" },
  dl: { label: "Driving Licence", color: "blue" },
  passport: { label: "Passport", color: "blue" },
  addressproof: { label: "Current Address Proof", color: "blue" },
  embassyletter: { label: "Embassy Letter", color: "blue" },
  admit: { label: "Admit Letter", color: "violet" },
  marksheet12: { label: "12th Marksheet", color: "violet" },
  marksheetug: { label: "UG Marksheet", color: "violet" },
  entrance: { label: "Entrance Scorecard (IELTS/GRE)", color: "violet" },
  bank: { label: "Bank Statement", color: "amber" },
  salary: { label: "Salary Slip / Form 16", color: "amber" },
  gst: { label: "GST Certificate", color: "amber" },
  coi: { label: "Certificate of Incorporation", color: "amber" },
  shopact: { label: "Shop Act Licence", color: "amber" },
  udyog: { label: "Udyam Aadhaar", color: "amber" },
  license: { label: "Professional Licence (CA/CS/Dr)", color: "amber" },
  pension: { label: "Pension Slip", color: "amber" },
  incomecert: { label: "Income Certificate", color: "amber" },
  itr: { label: "Income Tax Return", color: "amber" },
  property: { label: "Property Documents", color: "violet" },
  unknown: { label: "Unidentified", color: "gray" },
};

export const ID_TYPES = ["aadhaar", "voter", "dl", "passport"];
export const OCCUPATIONS = ["Salaried", "Self-employed", "Professional", "Pensioner", "Other"];
export const RELATIONS = ["Father", "Mother", "Spouse", "Sibling", "Guardian", "Other"];
export const COURSES = ["Bachelors abroad", "Masters abroad", "MBA", "PhD / Doctorate"];
export const COUNTRIES = ["United Kingdom", "United States", "Canada", "Australia", "Germany", "Ireland"];

export const OCC_PROOFS = {
  Salaried: { label: "Salary Slip", types: ["salary"] },
  "Self-employed": { label: "GST / COI / Shop Act / Udyam", types: ["gst", "coi", "shopact", "udyog"] },
  Professional: { label: "Professional Licence", types: ["license"] },
  Pensioner: { label: "Pension Slip", types: ["pension"] },
  Other: { label: "Income Certificate", types: ["incomecert"] },
};

export const ROLE_LABELS = { applicant: "Applicant", "co-borrower": "Financial co-borrower", secondary: "Secondary co-borrower" };
export const ROLE_SHORT = { applicant: "Applicant", "co-borrower": "Co-borrower", secondary: "Secondary" };

export const CHECK_DEFS = [
  { key: "pan_dedupe", title: "PAN dedupe", source: "Internal bureau + NSDL", detail: "Checks for existing loans on the same PAN", auto: true },
  { key: "id_check", title: "ID validation", source: "Issuing authority registry", detail: "Validates the ID proof against the issuer", auto: true },
  { key: "income_tax", title: "Income tax check", source: "Income Tax Department", detail: "Cross-checks declared income with ITR data", auto: true, financialOnly: true },
  { key: "occupation", title: "Occupation check", source: "Category-level verification", detail: "Validates occupation proof at category level", auto: true, financialOnly: true },
  { key: "ckyc", title: "CKYC retrieval", source: "Central KYC Registry", detail: "Customer OTP required to fetch KYC record", auto: false },
  { key: "digilocker", title: "DigiLocker pull", source: "DigiLocker (Govt of India)", detail: "Customer OTP required to pull issued documents", auto: false },
];

export const isFinancial = (party, loan) => party.role === "co-borrower" || (party.role === "applicant" && loan.selfCoBorrower);

export const checksFor = (party, loan) => CHECK_DEFS.filter((d) => !d.financialOnly || isFinancial(party, loan));

export const initials = (n) => n.split(" ").filter(Boolean).map((x) => x[0]).slice(0, 2).join("").toUpperCase();

export function checklistFor(party, loan, addressFlow) {
  const items = [
    { label: "PAN Card", types: ["pan"], required: true },
    { label: "ID Proof", hint: "Any one — Aadhaar, Voter ID, Driving Licence or Passport", types: ID_TYPES, required: true },
    { label: "Photograph", hint: "Passport-size photo", types: ["photo"], required: true },
  ];
  if (party.role === "applicant") {
    items.push({ label: "Admit Letter", types: ["admit"], required: true });
    items.push({ label: "12th Marksheet", types: ["marksheet12"], required: true });
    items.push({ label: "Entrance Scorecard", hint: "IELTS / GRE / GMAT / TOEFL", types: ["entrance"], required: true });
    if (addressFlow?.otherSame === false) {
      if (addressFlow.docAddressType === "current") items.push({ label: "Permanent Address Proof", hint: "Any ID — Aadhaar / Voter ID / Driving Licence / Passport", types: ["addressproof", ...ID_TYPES], required: true, tag: "Address" });
      else if (addressFlow.currentLocation === "india") items.push({ label: "Current Address Proof", hint: "Any ID — Aadhaar / Voter ID / Driving Licence / Passport", types: ["addressproof", ...ID_TYPES], required: true, tag: "Address" });
      else if (addressFlow.currentLocation === "abroad") items.push({ label: "Embassy Letter", hint: "Letter from the embassy confirming the current address abroad", types: ["embassyletter"], required: true, tag: "Address" });
    }
    if (loan.collateral) items.push({ label: "Property Documents", types: ["property"], required: true, tag: "Collateral" });
  }
  const financial = isFinancial(party, loan);
  if (financial || party.role === "secondary") {
    const req = financial;
    const occ = OCC_PROOFS[party.occupation] || OCC_PROOFS.Other;
    items.push({ label: "Bank Statement", types: ["bank"], required: req });
    items.push({ label: occ.label, hint: `Occupation proof — ${party.occupation}`, types: occ.types, required: req, tag: "Occupation" });
    items.push({ label: "Income Tax Return", types: ["itr"], required: req });
  }
  return items;
}

const NAME_RULES = [
  ["photo", "photo"], ["udyam", "udyog"], ["udyog", "udyog"], ["pan", "pan"], ["aadhaar", "aadhaar"], ["aadhar", "aadhaar"],
  ["voter", "voter"], ["driving", "dl"], ["passport", "passport"], ["embassy", "embassyletter"], ["admit", "admit"], ["offer", "admit"],
  ["12th", "marksheet12"], ["hsc", "marksheet12"], ["marksheet", "marksheet12"], ["ielts", "entrance"],
  ["gre", "entrance"], ["gmat", "entrance"], ["toefl", "entrance"], ["bank", "bank"], ["statement", "bank"],
  ["salary", "salary"], ["payslip", "salary"], ["form16", "salary"], ["form 16", "salary"], ["gst", "gst"],
  ["itr", "itr"], ["income tax", "itr"], ["property", "property"], ["deed", "property"], ["pension", "pension"],
  ["licence", "license"], ["license", "license"], ["incorporation", "coi"], ["shop act", "shopact"],
];

export function classifyByName(name) {
  const n = name.toLowerCase();
  const hit = NAME_RULES.find(([kw]) => n.includes(kw));
  return hit ? hit[1] : "unknown";
}

const APPLICANT_TYPES = ["admit", "marksheet12", "entrance"];
const FINANCIAL_TYPES = ["bank", "salary", "gst", "coi", "shopact", "udyog", "license", "pension", "incomecert", "itr"];

export function guessParty(type, parties) {
  if (APPLICANT_TYPES.includes(type)) return parties.find((p) => p.role === "applicant")?.id || null;
  if (FINANCIAL_TYPES.includes(type)) return parties.find((p) => p.role === "co-borrower")?.id || null;
  return null;
}

export const defaultVerifs = () => ({ pan_dedupe: "pending", id_check: "pending", income_tax: "pending", occupation: "pending", ckyc: "otp", digilocker: "otp" });

export const seedParties = [
  { id: "p1", role: "applicant", name: "Rohan Kulkarni", email: "rohan.k@gmail.com", phone: "+91 98220 12941", relation: "Self", occupation: "Salaried", consent: "given" },
  { id: "p2", role: "co-borrower", name: "Ajay Kulkarni", email: "ajay.k@gmail.com", phone: "+91 98220 34567", relation: "Father", occupation: "Salaried", consent: "given" },
];

export const seedDocs = [
  { id: "d1", name: "PAN Card.pdf", size: "1.2 MB", type: "pan", partyId: "p1", status: "Verified", confidence: 99 },
  { id: "d2", name: "Aadhaar Card.pdf", size: "2.4 MB", type: "aadhaar", partyId: "p1", status: "Verified", confidence: 98 },
  { id: "d3", name: "Admit Letter — University of Leeds.pdf", size: "640 KB", type: "admit", partyId: "p1", status: "Verified", confidence: 97 },
  { id: "d4", name: "PAN Card (Ajay).pdf", size: "1.1 MB", type: "pan", partyId: "p2", status: "Verified", confidence: 99 },
  { id: "d5", name: "Form 16 — FY 2023-24.pdf", size: "840 KB", type: "salary", partyId: "p2", status: "Classified", confidence: 87 },
  { id: "d6", name: "scan_0412.pdf", size: "2.9 MB", type: "unknown", partyId: null, status: "Unidentified", confidence: 0 },
];

export const seedProfiles = {
  p1: [
    { k: "Full name", v: "Rohan Ajay Kulkarni", src: "PAN Card", srcType: "pan", conf: 99 },
    { k: "PAN number", v: "FJTPK4821L", src: "PAN Card", srcType: "pan", conf: 99 },
    { k: "Date of birth", v: "14 August 2002", src: "PAN Card", srcType: "pan", conf: 98 },
    { k: "Aadhaar number", v: "XXXX XXXX 4291", src: "Aadhaar Card", srcType: "aadhaar", conf: 98 },
    { k: "Permanent address", v: "18, Shivaji Housing Society, Kothrud, Pune 411038", src: "Aadhaar Card", srcType: "aadhaar", conf: 97 },
    { k: "Gender", v: "Male", src: "Aadhaar Card", srcType: "aadhaar", conf: 99 },
    { k: "University", v: "University of Leeds", src: "Admit Letter", srcType: "admit", conf: 96 },
    { k: "Course & intake", v: "MSc Data Science · Sep 2026", src: "Admit Letter", srcType: "admit", conf: 88, note: "Intake month was partially unclear on the admit letter" },
    { k: "Tuition fee", v: "£ 24,500 / year", src: "Admit Letter", srcType: "admit", conf: 94 },
    { k: "Programme start", v: "22 September 2026", src: "Admit Letter", srcType: "admit", conf: 95 },
    { k: "Passport number", v: "", src: "Not found in documents", conf: 0, manual: true },
    { k: "Years of experience", v: "", src: "Not found in documents", conf: 0, manual: true },
  ],
  p2: [
    { k: "Full name", v: "Ajay Kulkarni", src: "PAN Card", srcType: "pan", conf: 98 },
    { k: "PAN number", v: "BKJPK1932M", src: "PAN Card", srcType: "pan", conf: 99 },
    { k: "Date of birth", v: "2 March 1972", src: "PAN Card", srcType: "pan", conf: 97 },
    { k: "Employer", v: "TCS Ltd.", src: "Form 16", srcType: "salary", conf: 87, note: "Salary slip says “Tata Consultancy Services” — confirm the correct name" },
    { k: "Monthly income", v: "₹ 1,24,500", src: "Form 16", srcType: "salary", conf: 91 },
    { k: "Employer TAN", v: "PNET04521B", src: "Form 16", srcType: "salary", conf: 96 },
    { k: "Assessment year", v: "AY 2025-26", src: "Form 16", srcType: "salary", conf: 95 },
    { k: "TDS deducted", v: "₹ 1,86,300", src: "Form 16", srcType: "salary", conf: 94 },
    { k: "Years of experience", v: "", src: "Not found in documents", conf: 0, manual: true },
    { k: "Designation", v: "", src: "Not found in documents", conf: 0, manual: true },
  ],
};

const EXTRACTS = {
  photo: (p) => [{ k: "Photograph", v: "Clear · white background · face match ok", conf: 97 }],
  pan: (p) => [{ k: "Full name", v: p.name, conf: 98 }, { k: "PAN number", v: "XXTPK0000X", conf: 99 }, { k: "Date of birth", v: "12 June 1975", conf: 96 }],
  aadhaar: (p) => [{ k: "Aadhaar number", v: "XXXX XXXX 8817", conf: 98 }, { k: "Permanent address", v: "Pune, Maharashtra", conf: 97 }],
  voter: (p) => [{ k: "Voter ID number", v: "MT/07/042/312981", conf: 97 }, { k: "Address (Voter ID)", v: "Pune, Maharashtra", conf: 95 }],
  dl: (p) => [{ k: "Licence number", v: "MH12 20190012345", conf: 96 }, { k: "Licence valid till", v: "March 2039", conf: 97 }],
  passport: (p) => [{ k: "Passport number", v: "U8823917", conf: 98 }, { k: "Passport expiry", v: "August 2033", conf: 97 }],
  addressproof: (p) => [{ k: "Current address", v: "Flat 4B, Gera Trinity, Kharadi, Pune 411014", conf: 95 }],
  admit: (p) => [{ k: "University", v: "University of Leeds", conf: 96 }, { k: "Course & intake", v: "MSc Data Science · Sep 2026", conf: 88 }],
  marksheet12: (p) => [{ k: "12th board", v: "Maharashtra HSC", conf: 97 }, { k: "12th percentage", v: "88.6%", conf: 96 }, { k: "Year of passing", v: "2020", conf: 98 }],
  entrance: (p) => [{ k: "Test & score", v: "IELTS · 7.5 band", conf: 96 }, { k: "Test date", v: "January 2026", conf: 95 }],
  bank: (p) => [{ k: "Bank & account", v: "HDFC Bank ····4521", conf: 96 }, { k: "Avg monthly balance", v: "₹ 86,400", conf: 92, note: "Two months show a dip below ₹ 50,000 — verify with the customer" }, { k: "Statement period", v: "Dec 2025 – May 2026", conf: 95 }],
  salary: (p) => [{ k: "Employer", v: "Tata Consultancy Services", conf: 94 }, { k: "Monthly income", v: "₹ 1,24,500", conf: 91 }],
  gst: (p) => [{ k: "GSTIN", v: "27ABCDE1234F1Z5", conf: 97 }, { k: "Business name", v: `${p.name.split(" ")[0]} Enterprises`, conf: 93 }],
  coi: (p) => [{ k: "CIN", v: "U74999PN2015PTC157788", conf: 96 }],
  shopact: (p) => [{ k: "Shop Act licence no.", v: "PN-2019-044512", conf: 95 }],
  udyog: (p) => [{ k: "Udyam number", v: "UDYAM-MH-26-0012345", conf: 96 }],
  license: (p) => [{ k: "Licence body & number", v: "ICAI · 118201", conf: 96 }],
  pension: (p) => [{ k: "Monthly pension", v: "₹ 42,800", conf: 94 }, { k: "PPO number", v: "PPO/2018/44821", conf: 96 }],
  incomecert: (p) => [{ k: "Certified annual income", v: "₹ 3,60,000", conf: 92, note: "Certificate is over 10 months old — confirm it's still valid" }],
  itr: (p) => [{ k: "ITR gross income", v: "₹ 15,10,000", conf: 95 }, { k: "Assessment year (ITR)", v: "AY 2025-26", conf: 97 }],
  property: (p) => [{ k: "Property type", v: "Residential flat · Kharadi, Pune", conf: 94 }, { k: "Market value (est.)", v: "₹ 1.2 Cr", conf: 90, note: "Estimated from sale deed — formal valuation pending" }],
};

export function extractsForDoc(type, party) {
  const fn = EXTRACTS[type];
  if (!fn) return [];
  return fn(party).map((e) => ({ ...e, src: DOC_TYPES[type].label, srcType: type }));
}

export const seedApplications = [
  { id: "EDU-240184", name: "Rohan Kulkarni", programme: "MSc Data Science", university: "University of Leeds", country: "UK", live: true, ftr: 0, status: "In progress", updated: "Today", parties: 2, docs: 6 },
  { id: "EDU-240171", name: "Sneha Patil", programme: "MS Computer Science", university: "TU Munich", country: "Germany", ftr: 100, status: "Query raised", updated: "Yesterday", parties: 3, docs: 14 },
  { id: "EDU-240166", name: "Arjun Mehta", programme: "MBA", university: "INSEAD", country: "France", ftr: 92, status: "Ready", updated: "2 days ago", parties: 2, docs: 11 },
  { id: "EDU-240158", name: "Fatima Sheikh", programme: "MEng Robotics", university: "NUS", country: "Singapore", ftr: 61, status: "In progress", updated: "3 days ago", parties: 3, docs: 8 },
  { id: "EDU-240149", name: "Karan Nair", programme: "BSc Economics", university: "UBC", country: "Canada", ftr: 34, status: "Draft", updated: "5 days ago", parties: 1, docs: 3 },
  { id: "EDU-240132", name: "Vikram Joshi", programme: "MBA", university: "ISB Hyderabad", country: "India", ftr: 88, status: "Declined", updated: "1 week ago", parties: 1, docs: 9 },
];

export const seedStatusData = {
  "EDU-240171": {
    stage: "query",
    assignee: { name: "Meera Iyer", role: "Senior Credit Officer", since: "yesterday" },
    sla: "Response due in 2 days",
    timeline: [
      { label: "Submitted by sales", date: "27 Jun", done: true },
      { label: "Credit review started", date: "28 Jun", done: true },
      { label: "Queries raised", date: "Today", done: true, current: true },
      { label: "Final decision", date: "Pending", done: false },
    ],
    decision: null,
    queries: [
      { id: "q1", type: "document", title: "Latest 3 months' bank statement", msg: "The statement on file ends in March. Please share April–June for the co-borrower.", by: "Meera Iyer", date: "Today, 11:20", status: "open" },
      { id: "q2", type: "clarification", title: "Employer name mismatch", msg: "Form 16 says “TCS Ltd.” while salary slips say “Tata Consultancy Services”. Confirm the registered employer name.", by: "Meera Iyer", date: "Today, 11:24", status: "open" },
      { id: "q3", type: "document", title: "Property valuation report", msg: "Share the formal valuation report for the pledged property.", by: "Meera Iyer", date: "Yesterday", status: "resolved", response: "Uploaded “valuation_report.pdf”" },
    ],
  },
  "EDU-240132": {
    stage: "declined",
    assignee: { name: "Rajesh Khanna", role: "Credit Manager", since: "3 days ago" },
    sla: "Appeal window: 12 days left",
    timeline: [
      { label: "Submitted by sales", date: "12 Jun", done: true },
      { label: "Credit review started", date: "14 Jun", done: true },
      { label: "Queries resolved", date: "18 Jun", done: true },
      { label: "Declined by credit", date: "20 Jun", done: true, current: true, bad: true },
    ],
    decision: { type: "declined", reason: "Co-borrower's fixed obligations to income ratio (FOIR) exceeds policy threshold at 68%. Declined per credit policy CP-114." },
    queries: [
      { id: "q1", type: "clarification", title: "Existing loan obligations", msg: "Confirm the outstanding EMIs on the co-borrower's home loan.", by: "Rajesh Khanna", date: "16 Jun", status: "resolved", response: "EMI ₹ 38,500/month · 11 years remaining — confirmed with customer" },
    ],
  },
};

export const seedVerifs = {
  p1: { pan_dedupe: "verified", id_check: "verified", income_tax: "pending", occupation: "pending", ckyc: "otp", digilocker: "otp" },
  p2: { pan_dedupe: "verified", id_check: "progress", income_tax: "progress", occupation: "pending", ckyc: "otp", digilocker: "otp" },
};
